/**
 * Top-level layout for the pharma questionnaire review page.
 *
 * Orchestrator only — every piece of state and side-effect lives in a
 * dedicated hook, so this file reads top-to-bottom as
 * "data → derived → handlers → JSX" without business logic mixed in.
 *
 * @module pharmaQuestionaire/pharmaQuestionaireLayout
 */
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { LoaderMessage } from "../../components/loaderMessage/loaderMessage";
import { ModalId } from "../../enums/modalId";
import { OrganizationType } from "../../enums/organizationTypes";
import { useBranchedPharmaQuestions } from "../../hooks/useBranchedPharmaQuestions";
import { useMergePdfs } from "../../hooks/useMergePdfs";
import { useModalOpener } from "../../hooks/useModalOpener";
import { setOpenedModalId } from "../../redux/slice/modalSliceNew";
import { AppDispatch, RootState } from "../../redux/store/store";
import { logDataToConsole } from "../../utils/customLogger";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import PharmaQuestionnaireBody from "./components/PharmaQuestionnaireBody";
import PharmaQuestionnaireHeader from "./components/PharmaQuestionnaireHeader";
import QuestionnaireFooter from "./components/QuestionnaireFooter";
import QuestionnaireModals from "./components/QuestionnaireModals";
import { useFetchDocument } from "./hooks/useFetchDocument";
import { useFileUploadCleanup } from "./hooks/useFileUploadCleanup";
import { useMnJudgeAssessmentPreview } from "./hooks/useMnJudgeAssessmentPreview";
import { usePharmaQuestionnaireSubmission } from "./hooks/usePharmaQuestionnaireSubmission";
import { useQuestionnaireState } from "./hooks/useQuestionnaireState";
import { useQuestionnaireWorkflowDocument } from "./hooks/useQuestionnaireWorkflowDocument";
import { useSingleCmmOrder } from "./hooks/useSingleCmmOrder";
import { buildUploadFilePath } from "./utils/buildUploadFilePath";
import { isPrimaryCareFeature } from "./utils/isPrimaryCareFeature";

/**
 * Pharma questionnaire review page layout.
 *
 * Composes header, body, footer, and modals (including Prediction Summary
 * from development) while keeping data fetching and submission in hooks.
 *
 * @returns Questionnaire review page UI
 */
const PharmaQuestionaireLayout = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { id } = useParams();
  const orgId = getOrgIdForFetchExternalWorklist();
  const isPrimaryCare = useMemo(() => isPrimaryCareFeature(), []);

  // -------- data ---------------------------------------------------------
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { singleCmmOrderData, singleCmmOrderDataLoading } =
    useSingleCmmOrder(id);
  const { data: questionnaireReview, loading: questionnaireReviewLoading } =
    useBranchedPharmaQuestions(id);
  const {
    fileNames,
    setFileNames,
    pdfUrl,
    questionnaire,
    initialQuestionnaire,
  } = useQuestionnaireState(questionnaireReview);
  const { documents, setDocuments, isFetchingDoc, errorInFetching } =
    useFetchDocument(pdfUrl);
  const { mergePdfsFromBase64, isMerging, mergedPdfToRender } = useMergePdfs();
  const { openedModalId } = useModalOpener();

  // -------- derived ------------------------------------------------------
  const uploadFilePath = useMemo(
    () => buildUploadFilePath(orgId, id),
    [orgId, id],
  );
  const predictionSummaryIdentifier = useMemo(
    () => id ?? questionnaireReview?.details?.identifier ?? "",
    [id, questionnaireReview?.details?.identifier],
  );

  // -------- local UI state ----------------------------------------------
  const { workflowDocument, handleWorkflowDocumentChange } =
    useQuestionnaireWorkflowDocument();
  const [, setIsQuestionnaireValid] = useState<boolean>(false);
  const handleValidityChange = useCallback(
    (isValid: boolean) => setIsQuestionnaireValid(isValid),
    [],
  );

  // -------- side-effect hooks -------------------------------------------
  const {
    handleSubmit,
    submitQuestionnaire,
    finishSubmission,
    saving,
    progress,
  } = usePharmaQuestionnaireSubmission({
    orderId: id,
    workflowDocument,
    questionnaireReview,
    mergedPdfToRender,
    uploadFilePath,
    userEmail: user?.email ?? "",
  });
  useFileUploadCleanup();
  const { decision: mnJudgeDecision, confidence: mnJudgeConfidence } =
    useMnJudgeAssessmentPreview(predictionSummaryIdentifier);

  // -------- handlers -----------------------------------------------------
  const handleGoBack = useCallback(
    () => navigate(`/pharma-pa-worklists/pharma-pa-form/${id}`),
    [navigate, id],
  );
  const handleCancelClick = useCallback(() => {
    logDataToConsole("Cancel clicked");
    handleGoBack();
  }, [handleGoBack]);

  const isAstera = orgId === OrganizationType.ASTERA;

  const openSubmissionModal = useCallback(() => {
    if (isAstera) {
      dispatch(
        setOpenedModalId({
          id: ModalId.ASTERA_SUBMISSION_MODAL,
          metaData: { data: singleCmmOrderData },
        }),
      );
    } else {
      dispatch(setOpenedModalId({ id: ModalId.SUBMISSION_SUMMARY_MODAL }));
    }
  }, [dispatch, isAstera, singleCmmOrderData]);

  /**
   * "Submit Case" hands off to the submission modal, where the operator picks
   * between sending to delivery only or submitting the questionnaire too.
   * Primary care has no submission modal, so it submits directly.
   *
   * Astera is checked first because the footer labels itself "Submit Key" off
   * the org alone: an Astera org that also carries the primaryCare feature
   * would otherwise submit directly from a button promising the Submit Onco
   * modal.
   */
  const handleSubmitCase = useCallback(() => {
    if (isPrimaryCare && !isAstera) {
      handleSubmit();
      return;
    }
    openSubmissionModal();
  }, [isPrimaryCare, isAstera, handleSubmit, openSubmissionModal]);

  /**
   * Post-flight handling for "Submit and send to delivery": navigate away only
   * when both requests landed; the modal owns the save-failure toast.
   */
  const handleSubmitCaseComplete = useCallback(
    (bothSucceeded: boolean, isCaseSubmitted: boolean) => {
      if (bothSucceeded) {
        finishSubmission(true);
        return;
      }
      if (!isCaseSubmitted) finishSubmission(false);
    },
    [finishSubmission],
  );

  const openManageAttachments = useCallback(
    () => dispatch(setOpenedModalId({ id: ModalId.DOCUMENT_MERGE_MODAL })),
    [dispatch],
  );
  const openPredictionSummary = useCallback(
    () => dispatch(setOpenedModalId({ id: ModalId.PREDICTION_SUMMARY_MODAL })),
    [dispatch],
  );
  const closeDocumentsModal = useCallback(
    () => dispatch(setOpenedModalId({ id: "" })),
    [dispatch],
  );

  // -------- render -------------------------------------------------------
  if (singleCmmOrderDataLoading || questionnaireReviewLoading) {
    return (
      <div className="pharma-questionaire-form__container flex h-full flex-col bg-[#F7F9FA] px-[0.9375rem] py-[0.625rem]">
        <LoaderMessage message={"Fetching relevant information..."} />
      </div>
    );
  }

  return (
    <div className="pharma-questionaire-form__container flex h-full flex-col bg-[#F7F9FA] px-[0.9375rem] py-[0.625rem]">
      <PharmaQuestionnaireHeader onBack={handleGoBack} />
      <PharmaQuestionnaireBody
        singleCmmOrderData={singleCmmOrderData}
        documents={documents}
        isFetchingDoc={isFetchingDoc}
        errorInFetching={errorInFetching}
        mergedPdfToRender={mergedPdfToRender}
        questionnaire={questionnaire}
        initialQuestionnaire={initialQuestionnaire}
        onValidityChange={handleValidityChange}
        onWorkflowDocumentChange={handleWorkflowDocumentChange}
        onManageAttachments={openManageAttachments}
        onPredictionSummary={openPredictionSummary}
        mnJudgeDecision={mnJudgeDecision}
        mnJudgeConfidence={mnJudgeConfidence}
      />
      <QuestionnaireFooter
        orgId={orgId}
        submitting={saving}
        onCancel={handleCancelClick}
        onSubmit={handleSubmitCase}
        isQuestionnaireValid={true}
        progress={progress}
      />
      <QuestionnaireModals
        onCloseDocumentsModal={closeDocumentsModal}
        identifier={questionnaireReview?.details?.identifier ?? ""}
        predictionSummaryIdentifier={predictionSummaryIdentifier}
        documents={documents}
        fileNames={fileNames}
        setFileNames={setFileNames}
        setDocuments={setDocuments}
        openedModalId={openedModalId ?? ""}
        singleCmmOrderData={singleCmmOrderData}
        mergePdfsFromBase64={mergePdfsFromBase64}
        isMerging={isMerging}
        onSubmitCase={submitQuestionnaire}
        onSubmitCaseComplete={handleSubmitCaseComplete}
      />
    </div>
  );
};

export default PharmaQuestionaireLayout;
